// Platform-only permissions — keys that are NEVER satisfiable through tenant
// RBAC. Not by an explicit role grant, not by a `module.*` wildcard, and not by
// the Owner `['*']` wildcard. They are authorized exclusively on the
// platform-staff plane (apps/admin / platform_admin_role), which is distinct
// from tenant role resolution.
//
// WHY a hard exclusion and not "just omit it from the grant catalog": the Owner
// role carries `['*']`, which textually matches every concrete permission key.
// Omitting a key from permission-groups.ts removes it from the role-builder UI
// but still leaves it grantable to Owner (and to any code path that expands
// `'*'`). The runtime matcher (`permissionMatches`) and the custom-role
// validator both consult this set, so a platform-only key can never resolve to
// `true` on the tenant plane regardless of how a role is constructed.
//
// `assistant.code.trace` renders RAW proprietary platform source code (file
// paths, symbol names, snippets). It is platform/support-staff only — see
// docs/assistant/code-intel-source-exposure-policy.md. A tenant operator of any
// role, including Owner, must never hold it.
export const PLATFORM_ONLY_PERMISSIONS = new Set([
    // D-64 / 03-SECURITY-AND-ABUSE-MODEL.md §3.6: the PR3 breaker targets
    // another tenant and is platform-operator only. Register the exclusion
    // with its parked permission now so Owner '*' and custom roles cannot grant it.
    'payment-hub.admin.breaker',
    'assistant.code.trace',
    // Tip-outbox replay is an ops recovery tool (rebuilds a tenant's tip ledger
    // from the payments outbox after a consumer outage). The route takes the
    // TARGET tenantId in the request body, so a tenant-plane grant would let an
    // Owner of tenant A replay — and read ledger aggregates of — tenant B.
    // Platform-staff plane only (X-Platform-Admin-Token via adminTokenMw).
    'workforce.tips.replay',
    // Surcharge-charging tenant enrollment (MPR-3 PR-E2b) — cross-tenant write
    // onto payments.surcharge_tenant_allowlist, gated by counsel review
    // (migration 0454's own header) AND a validated GL account (this route's
    // own COA pre-flight gate). Admin-token-only route, same
    // ops-recovery-tool shape as workforce.tips.replay above — never a tenant
    // Owner action, regardless of role.
    'payments.surcharge-allowlist.enroll',
    // ACH processor ceiling write (#14485) -- cross-tenant ops write, admin-token-only route, same
    // posture as the surcharge-allowlist enrollment above: never a tenant Owner action.
    'payments.ach-limits.set',
]);
/** True if `key` is a platform-only permission (never grantable via tenant RBAC). */
export function isPlatformOnlyPermission(key) {
    return PLATFORM_ONLY_PERMISSIONS.has(key);
}
//# sourceMappingURL=platform-only-permissions.js.map